import * as cornerstone from '@cornerstonejs/core';
import { imageLoadPoolManager, Enums } from '@cornerstonejs/core';
import {
  init as csToolsInit,
  PanTool,
  ZoomTool,
  WindowLevelTool,
  LengthTool,
  RectangleROITool,
  AngleTool,
  EllipticalROITool,
  addTool,
} from '@cornerstonejs/tools';
import { registerWebImageLoader } from '@/features/study-detail/Viewer/registerWebImageLoader';

let initPromise: Promise<void> | null = null;
let isInitialized = false;

/**
 * Centralized Cornerstone initialization.
 *
 * This function handles ALL Cornerstone setup in one place:
 * - Core initialization
 * - Tools initialization
 * - Tool registration (Pan, Zoom, WindowLevel, etc.)
 * - Cache configuration (2GB)
 * - Web image loader registration
 *
 * Safe to call multiple times - uses Promise-based singleton pattern.
 * If called concurrently, all callers wait for the same initialization.
 */
export async function initializeCornerstone(): Promise<void> {
  // Return existing promise if init is in progress
  if (initPromise) return initPromise;

  // Return immediately if already initialized
  if (isInitialized) return Promise.resolve();

  initPromise = (async () => {
    // 1. Initialize Cornerstone core
    // Cornerstone v4 uses ContextPoolRenderingEngine by default for efficient WebGL context management
    // This pools 8 WebGL contexts across 16 viewports, preventing browser limits
    await cornerstone.init();

    // 2. Initialize tools
    await csToolsInit();

    // 3. Register all tools
    addTool(PanTool);
    addTool(ZoomTool);
    addTool(WindowLevelTool);
    addTool(LengthTool);
    addTool(RectangleROITool);
    addTool(AngleTool);
    addTool(EllipticalROITool);

    // 4. Configure cache (2GB)
    // Access cache directly from cornerstone module
    if (cornerstone.cache) {
      cornerstone.cache.setMaxCacheSize(2 * 1024 * 1024 * 1024);
    }

    // 5. Configure image load pool for multi-viewport scenario (16 viewports)
    // Interaction: User-initiated actions (highest priority) - 6 concurrent
    imageLoadPoolManager.setMaxSimultaneousRequests(
      Enums.RequestType.Interaction,
      6
    );
    // Prefetch: Background loading for warm-up - 16 concurrent (1 per viewport)
    imageLoadPoolManager.setMaxSimultaneousRequests(
      Enums.RequestType.Prefetch,
      16
    );
    // Thumbnail: Low priority for thumbnails - 2 concurrent
    imageLoadPoolManager.setMaxSimultaneousRequests(
      Enums.RequestType.Thumbnail,
      2
    );

    // 6. Register web image loader
    registerWebImageLoader();

    isInitialized = true;
  })();

  return initPromise;
}

/**
 * Check if Cornerstone has been initialized.
 * Useful for conditional logic that depends on initialization state.
 */
export function isCornerstoneInitialized(): boolean {
  return isInitialized;
}
